import { formatISO, parseISO } from 'date-fns';
import type GlobalUI from 'state/UI/types';
import { type Organisation, type Period, type ReportingStandard, type Tag } from 'types/models';

const formatDate = (date: string | Date) => formatISO(typeof date === 'string' ? parseISO(date) : date, { representation: 'date' });

/**
 * Query params for /statistics/kpisinfo from the Key Figure Status filters. In consolidation mode the request keeps the
 * active entity as `organisationId`: the backend consolidates its subsidiaries, and the selection only narrows the list
 * on the client. Sending the selection would make the backend treat its first entity as the consolidation root.
 */
export function getStatisticsFilterParams(filters: GlobalUI['statisticsFilters'], { includeSubsidiaries }: { includeSubsidiaries: boolean }) {
   const groupedFilter = Object.entries(filters).reduce((acc, [kind, filterItems = []]) => {
      switch (kind) {
         case 'periods':
            return {
               ...acc,
               ...[filterItems]
                  .flat()
                  .filter((item) => !!item)
                  .map((period) => ({ from: formatDate((period as Period).from), to: formatDate((period as Period).to) })),
            };
         case 'reportingStandards':
            return {
               ...acc,
               reportingStandardId: filterItems.map((filter) => (filter as ReportingStandard)?.id),
            };
         case 'organisations':
            return includeSubsidiaries
               ? acc
               : {
                    ...acc,
                    organisationId: filterItems.map((filter) => (filter as Organisation)?.id),
                 };
         case 'tags':
            return {
               ...acc,
               tag: filterItems.map((filter) => (filter as Tag)?.name),
            };
         default:
            return acc;
      }
   }, {});

   return Object.entries(groupedFilter).flatMap(([key, value]): [string, string][] =>
      Array.isArray(value) && value.length ? [[key, [value].flat().join(',')]] : []
   );
}
